
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

import {
  Color,
  ColorPickerType,
  ColorType,
  DEFAULT_LINEAR_GRADIENT,
  DEFAULT_RADIAL_GRADIENT,
  GlobalColor,
  GradientColor
} from "@/types/colors";
import VueColorWrapper from "@/components/Reusable/CustomInputs/3rdPartyWrappers/VueColorWrapper.vue";
import VueGradientWrapper from "@/components/Reusable/CustomInputs/3rdPartyWrappers/VueGradientWrapper.vue";

import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";

import ColorBadge from "@/components/Reusable/CustomInputs/Color/ColorSwatch.vue";
import GlobalColorsList from "@/components/Reusable/CustomInputs/Color/GlobalColorsList.vue";
import EyeDropper from "@/components/Reusable/CustomInputs/Color/EyeDropper.vue";
import Icon from "@/components/UI/Icon.vue";
import DropdownDragHandle from "@/components/Reusable/Dropdown/DropdownDragHandle.vue";
import { isGlobalColorAGradient } from "@/lib/colors/colors";

const DEFAULT_COLOR_CODE = "#642EDAFF";

type ColorTypeDropdownItem = { text: string; value: ColorType };

@Component({
  components: {
    DropdownDragHandle,
    Icon,
    EyeDropper,
    VueGradientWrapper,
    DropdownInput,
    Dropdown,
    GlobalColorsList,
    ColorBadge,
    VueColorWrapper
  }
})
export default class ColorPicker extends Vue {
  @Prop()
  value!: Color;

  componentKey = 0;

  @Prop({
    default() {
      return [];
    }
  })
  globalColors!: GlobalColor[];

  @Prop()
  globalColor!: GlobalColor;

  @Prop({ default: true })
  showGlobal!: boolean;

  @Prop({ default: false })
  isOpen!: boolean;

  @Prop({ default: true })
  canSave!: boolean;

  @Prop({ default: "solidOnly" })
  availableType!: ColorPickerType;

  type: ColorType = this.defaultType;

  get canvas(): Document | undefined {
    // Optional chaining because canvas document doesn't exist in sub-apps
    return window.Breakdance?.canvas?.document;
  }

  get internalColor() {
    return this.globalColor?.value || this.value.value || DEFAULT_COLOR_CODE;
  }

  get internalGradient() {
    return this.globalColor?.value || this.value;
  }

  get defaultType() {
    if (this.globalColor && isGlobalColorAGradient(this.globalColor)) {
      const gradient = this.globalColor.value as GradientColor;
      return gradient.type;
    }

    if (this.value) {
      return this.value.type;
    }

    return this.types[0].value; // Defaults to first type available
  }

  get types(): ColorTypeDropdownItem[] {
    const solidTypes: ColorTypeDropdownItem[] = [
      {
        text: "Solid",
        value: "solid"
      }
    ];

    const gradientTypes: ColorTypeDropdownItem[] = [
      {
        text: "Linear",
        value: "linear"
      },
      {
        text: "Radial",
        value: "radial"
      }
    ];

    if (this.availableType === "gradientOnly") {
      return gradientTypes;
    }

    if (this.availableType === "solidOnly") {
      return solidTypes;
    }

    return [...solidTypes, ...gradientTypes];
  }

  @Watch("isOpen", {
    immediate: true
  })
  maybeSetGradientWhenDialogOpens() {
    const hasValue = this.value && this.value?.value !== "";
    if (!this.isOpen || hasValue) return;
    this.setDefaultGradient();
  }

  @Watch("value")
  resetTypeWhenValueChanges() {
    this.type = this.defaultType;
  }

  resetValueWhenTypeChanges() {
    // Reset color to default when cycling between types
    if (this.type === "solid") {
      this.updateValue(null);
    } else {
      this.setDefaultGradient();
    }
  }

  setDefaultGradient() {
    if (this.type === "linear") {
      this.updateValue(DEFAULT_LINEAR_GRADIENT);
    } else if (this.type === "radial") {
      this.updateValue(DEFAULT_RADIAL_GRADIENT);
    }
  }

  onGlobalColorSelected(color: GlobalColor) {
    this.updateValue(`var(--${color.cssVariableName})`);
  }

  onSolidSelected(color: string) {
    this.updateValue(color);
  }

  onGradientSelected(gradient: GradientColor) {
    this.updateValue(gradient);
  }

  updateValue(newColor: Color | string | null) {
    this.$emit("input", newColor);
  }

  forceRerender() {
    // Every time that mouseup is called on the canvas,
    // our prop componentKey will change, and vue will destroy the picker and create a new one.
    this.canvas?.addEventListener("mouseup", () => this.componentKey++, {
      once: true
    });
  }
}
